import { useState } from 'react'
import { Lock, Pencil } from 'lucide-react'

import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { RichMarkdownEditor } from '@/components/RichMarkdownEditor'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { NoteMarkdown } from '@/components/NoteMarkdown'
import { formatServiceStatus } from '@/help/serviceStatus'
import { getServiceStatusMeta } from '@/lib/serviceStatusMeta'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import type { Addendum, AddendumChange } from '@/lib/schemas/db'

export interface AddendumsCardProps {
  resourceLabel: string
  status: 'Invoiced' | 'Cancelled'
  /** Oldest first. */
  addendums: Addendum[]
  getAuthorDisplayName: (authorId: string) => string
  /** When the record was locked, and by whom. */
  lockedAt?: string
  lockedByName?: string
  amending: boolean
  onStartAmend: () => void
  onDiscardAmend: () => void
  /** Changes staged in the form right now; drives the summary and Save state. */
  pendingChanges: AddendumChange[]
  onSaveAddendum: (
    reason: string,
    changes: AddendumChange[],
  ) => Promise<void> | void
  /** Omit both to hide the "show changes since" lens (e.g. Invoice Prep). */
  showDeltas?: boolean
  onToggleDeltas?: (next: boolean) => void
  saveError?: string | null
}

function ChangeRow(props: { change: AddendumChange }) {
  const { change } = props
  return (
    <li>
      <span className="text-slate-500">{change.label}:</span>{' '}
      <span className="text-rose-700/80 line-through decoration-rose-400">
        {change.before === '' ? '(empty)' : change.before}
      </span>{' '}
      <span className="text-slate-400">→</span>{' '}
      <span className="font-medium text-emerald-800">
        {change.after === '' ? '(cleared)' : change.after}
      </span>
    </li>
  )
}

/**
 * The lock status of an amended record: what it is, whether anything has been
 * added since, and the way in to add more.
 *
 * Deliberately quiet by default — a count and one button. The full trail is one
 * click away rather than filling the page, and the record itself keeps showing
 * plain current values unless "show changes" is on.
 */
export function AddendumsCard(props: AddendumsCardProps) {
  const {
    resourceLabel,
    status,
    addendums,
    getAuthorDisplayName,
    lockedAt,
    lockedByName,
    amending,
    onStartAmend,
    onDiscardAmend,
    pendingChanges,
    onSaveAddendum,
    showDeltas,
    onToggleDeltas,
    saveError,
  } = props

  const [browseOpen, setBrowseOpen] = useState(false)
  const [openAddendumId, setOpenAddendumId] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  /**
   * The changes as they stood when the dialog opened.
   *
   * Deliberately a snapshot rather than live `pendingChanges`: saving updates
   * the record's effective values and ends amend mode, which empties
   * `pendingChanges`. Reading it live meant the dialog could re-render mid-save
   * showing no summary — and then fail validation if saved again.
   */
  const [stagedChanges, setStagedChanges] = useState<AddendumChange[]>([])
  /**
   * The addendum number as it read when the dialog opened. Live-computing it
   * from `addendums.length` made the open dialog's title flip A1 -> A2 the
   * moment the optimistic insert landed.
   */
  const [stagedNumber, setStagedNumber] = useState(1)

  const meta = getServiceStatusMeta(status)
  const latest = addendums.length > 0 ? addendums[addendums.length - 1] : null
  const nextNumber = addendums.length + 1

  const openConfirm = () => {
    setReason('')
    setStagedChanges(pendingChanges)
    setStagedNumber(nextNumber)
    setConfirmOpen(true)
  }

  const closeConfirm = () => {
    setConfirmOpen(false)
    setReason('')
    setStagedChanges([])
  }

  const handleSave = async () => {
    const trimmed = reason.trim()
    if (!trimmed || isSaving || stagedChanges.length === 0) return
    setIsSaving(true)
    try {
      await onSaveAddendum(trimmed, stagedChanges)
      closeConfirm()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${meta.dotClass}`} />
          <span className={`text-[13px] font-semibold ${meta.textClass}`}>
            {formatServiceStatus(status)}
          </span>
          <Lock className="h-3.5 w-3.5 text-slate-500" aria-label="Locked" />
        </div>

        {amending ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-amber-700">
              {pendingChanges.length === 0
                ? 'Edit any fields below, then save.'
                : `${pendingChanges.length} change${pendingChanges.length === 1 ? '' : 's'}`}
            </span>
            <button
              type="button"
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              onClick={onDiscardAmend}
            >
              Discard
            </button>
            <button
              type="button"
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={pendingChanges.length === 0}
              onClick={openConfirm}
            >
              Save addendum A{nextNumber}…
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
            onClick={onStartAmend}
          >
            <Pencil className="h-3.5 w-3.5" />
            Amend record
          </button>
        )}
      </div>

      {addendums.length === 0 ? (
        <p className="text-xs text-slate-500">
          No addendums — this {resourceLabel.toLowerCase()} stands exactly as it
          was {status.toLowerCase()}
          {lockedByName ? ` by ${lockedByName}` : ''}
          {lockedAt ? ` on ${formatRelativeDateTime(lockedAt)}` : ''}.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              className="text-xs font-medium text-slate-700 hover:text-slate-900"
              onClick={() => setBrowseOpen((open) => !open)}
            >
              {addendums.length} addendum{addendums.length === 1 ? '' : 's'}
              {latest
                ? ` — latest by ${getAuthorDisplayName(latest.authorId)} · ${formatRelativeDateTime(latest.createdAt)}`
                : ''}{' '}
              {browseOpen ? '▴' : '▾'}
            </button>
            {onToggleDeltas ? (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-slate-300 accent-slate-900"
                  checked={showDeltas ?? false}
                  onChange={(event) => onToggleDeltas(event.target.checked)}
                />
                Show changes since {status.toLowerCase()}
              </label>
            ) : null}
          </div>

          {browseOpen ? (
            <ol className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {[...addendums].reverse().map((addendum) => {
                const number = addendums.indexOf(addendum) + 1
                const open = openAddendumId === addendum.id
                return (
                  <li key={addendum.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() =>
                        setOpenAddendumId(open ? null : addendum.id)
                      }
                    >
                      <span className="text-xs font-medium text-slate-800">
                        A{number} · {getAuthorDisplayName(addendum.authorId)}
                      </span>
                      <span className="text-[11px] text-slate-500">
                        {formatRelativeDateTime(addendum.createdAt)}{' '}
                        {open ? '▴' : '▾'}
                      </span>
                    </button>
                    {open ? (
                      <div className="space-y-1.5 border-t border-slate-100 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-600">
                          Reason: <NoteMarkdown text={addendum.reason} />
                        </div>
                        <ul className="space-y-1 text-[11px]">
                          {addendum.changes.map((change) => (
                            <ChangeRow key={change.field} change={change} />
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </li>
                )
              })}
              <li className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-slate-500">
                <Lock className="h-3 w-3" />
                {formatServiceStatus(status)} snapshot
                {lockedByName ? ` — locked by ${lockedByName}` : ''}
                {lockedAt ? ` on ${formatRelativeDateTime(lockedAt)}` : ''}
              </li>
            </ol>
          ) : null}
        </div>
      )}

      {amending ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Amending — the {resourceLabel.toLowerCase()} keeps its{' '}
          {status.toLowerCase()} values; your changes are recorded on top as
          addendum A{nextNumber}, with one reason covering all of them.
        </p>
      ) : null}

      {saveError ? (
        <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {saveError}
        </p>
      ) : null}

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => (open ? setConfirmOpen(true) : closeConfirm())}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save addendum A{stagedNumber}</DialogTitle>
            <DialogDescription>
              The {status.toLowerCase()} record is untouched — this is recorded
              on top of it, with your name and a timestamp.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <ul className="space-y-1.5 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
              {stagedChanges.map((change) => (
                <ChangeRow key={change.field} change={change} />
              ))}
            </ul>
            <div className="space-y-2">
              <FormCapsLabel>Reason for this addendum (required)</FormCapsLabel>
              {/* Keyed on the confirm session so each addendum starts from a
                  blank editor — tiptap holds its own document. */}
              <RichMarkdownEditor
                key={String(confirmOpen)}
                initialValue=""
                onChange={setReason}
                placeholder="One reason covering all the changes above"
                ariaLabel="Reason for this addendum"
              />
            </div>
            {saveError ? (
              <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800">
                {saveError} Nothing was recorded — you can try again.
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <button
              type="button"
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              disabled={isSaving}
              onClick={closeConfirm}
            >
              Back
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!reason.trim() || isSaving || stagedChanges.length === 0}
              onClick={handleSave}
            >
              {isSaving ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : null}
              Save addendum
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
